import React, { useState } from "react";
import {
  FiShield,
  FiPlus,
  FiSearch,
  FiCheckCircle,
  FiClock,
  FiArrowRight,
  FiFilter,
  FiExternalLink,
} from "react-icons/fi";
import type { RcaActionItem } from "../../../../types/rca.types.js";

const INITIAL_CAPA_ITEMS = [
  {
    id: "capa-1",
    capaCode: "CAPA-2026-0008",
    complaintCode: "CMP-2026-0001",
    rcaCode: "RCA-2026-0001",
    title: "CNC Spindle Thermal Drift Compensation & Tool Offset Recalibration",
    type: "CORRECTIVE",
    assignedTo: "John Smith",
    targetDate: "2026-09-05",
    status: "IN_PROGRESS",
    effectiveness: "PENDING_RUN",
    actionsCount: 3,
  },
  {
    id: "capa-2",
    capaCode: "CAPA-2026-0009",
    complaintCode: "CMP-2026-0002",
    rcaCode: "RCA-2026-0002",
    title: "Die-cast Mold De-gassing Vent Maintenance SOP",
    type: "PREVENTIVE",
    assignedTo: "Kumar",
    targetDate: "2026-09-12",
    status: "OPEN",
    effectiveness: "PENDING",
    actionsCount: 2,
  },
  {
    id: "capa-3",
    capaCode: "CAPA-2026-0004",
    complaintCode: "CMP-2026-0004",
    rcaCode: "RCA-2026-0004",
    title: "Line 4 Automated Vision Inspection Gate Installation",
    type: "CORRECTIVE",
    assignedTo: "Arun",
    targetDate: "2026-08-20",
    status: "COMPLETED",
    effectiveness: "VERIFIED",
    actionsCount: 4,
  },
];

export function RcaCapaActionsPage() {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("ALL");

  const filtered = INITIAL_CAPA_ITEMS.filter((c) => {
    const matchesSearch =
      !searchTerm.trim() ||
      c.capaCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.rcaCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "ALL" || c.type === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
            CMTS &gt; Actions & CAPA Governance
          </div>
          <h1 className="text-xl font-bold text-slate-900 mt-1">
            CAPA Actions & Provenance Handoff
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Track corrective and preventive actions handed over from RCA with
            Complaint provenance linkage.
          </p>
        </div>

        <button
          type="button"
          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-2"
        >
          <FiPlus className="w-4 h-4" />
          Create CAPA Handoff
        </button>
      </div>

      {/* Filter / Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <FiSearch className="absolute left-3.5 top-2.5 text-slate-400 w-4 h-4" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by CAPA code, RCA code, action title..."
            className="w-full pl-10 pr-4 py-2 text-xs rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-xs py-2 px-3 rounded-lg border border-slate-300 bg-white text-slate-700 font-medium focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">All Action Types</option>
            <option value="CORRECTIVE">Corrective Action (CAPA)</option>
            <option value="PREVENTIVE">Preventive Action</option>
          </select>
        </div>
      </div>

      {/* CAPA Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 uppercase text-[11px]">
              <tr>
                <th className="py-3 px-4">CAPA Code</th>
                <th className="py-3 px-4">Complaint / RCA Provenance</th>
                <th className="py-3 px-4">Action Title</th>
                <th className="py-3 px-4">Type</th>
                <th className="py-3 px-4">Assignee</th>
                <th className="py-3 px-4">Target Date</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-slate-50 transition-colors"
                >
                  <td className="py-3.5 px-4 font-bold text-blue-600">
                    {item.capaCode}
                  </td>
                  <td className="py-3.5 px-4">
                    <div className="font-semibold text-slate-800">
                      {item.complaintCode}
                    </div>
                    <div className="text-[11px] text-blue-600 font-medium">
                      {item.rcaCode}
                    </div>
                  </td>
                  <td className="py-3.5 px-4 font-medium text-slate-800 max-w-[280px]">
                    {item.title}
                  </td>
                  <td className="py-3.5 px-4">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${
                        item.type === "CORRECTIVE"
                          ? "bg-purple-50 text-purple-700 border-purple-200"
                          : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }`}
                    >
                      {item.type}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-slate-600 font-medium">
                    {item.assignedTo}
                  </td>
                  <td className="py-3.5 px-4 text-slate-600">
                    {item.targetDate}
                  </td>
                  <td className="py-3.5 px-4">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${
                        item.status === "COMPLETED"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-sky-50 text-sky-700 border-sky-200"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-right">
                    <button
                      type="button"
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-[11px] font-semibold shadow-sm inline-flex items-center gap-1"
                    >
                      Open <FiExternalLink className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
